/**
 * Storage Service - Provides abstraction over browser storage APIs
 * Benefits:
 * - Centralized error handling
 * - Easier testing and mocking
 * - Type-safe storage operations
 * - Consistent API across the application
 */

export interface StorageService {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

class SessionStorageService implements StorageService {
  getItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to get item from sessionStorage: ${key}`, error);
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to set item in sessionStorage: ${key}`, error);
    }
  }

  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from sessionStorage: ${key}`, error);
    }
  }

  clear(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error("Failed to clear sessionStorage", error);
    }
  }
}

class LocalStorageService implements StorageService {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to get item from localStorage: ${key}`, error);
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to set item in localStorage: ${key}`, error);
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from localStorage: ${key}`, error);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Failed to clear localStorage", error);
    }
  }
}

// Export singleton instances
export const sessionStorageService = new SessionStorageService();
export const localStorageService = new LocalStorageService();

// Storage keys constants - centralized key management
export const STORAGE_KEYS = {
  AUTH_REDIRECT_STATE: "auth_redirect_state",
  CDC_USER: "cdcUser",
  RELOADED: "reloaded",
  POST_LOGIN_REDIRECT: "post_login_redirect",
} as const;
